import React, { useEffect, useRef } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'

import SignupAcademyBook from './SignupAcademyBook'
import SignupVenueBook from './SignupVenueBook'
import SignupAuth from './SignupAuth'
import SignupCallback from './SignupCallback'
import SignupStart from './SignupStart'
import SetupWizardLayout from './SetupWizardLayout'
import SetupWizardLocations from './SetupWizardLocations'
import SetupWizardPolicies from './SetupWizardPolicies'
import SetupWizardSize from './SetupWizardSize'
import AcademyAccountGuard from './AcademyAccountGuard'
import AcademySignupGuard from './AcademySignupGuard'
import VenueSignupGuard from './VenueSignupGuard'
import CommunitySignupGuard from './CommunitySignupGuard'

/**
 * SignupRoutes — top-level wrapper for the entire /signup/* funnel.
 *
 * Owns the route slide+fade choreography:
 *   - Forward (Continue): incoming slides in from the right (+20px → 0),
 *     fades 0 → 1, 350ms with ease-out-expo.
 *   - Backward (Back):    incoming slides in from the left  (-20px → 0),
 *     fades 0 → 1, 350ms.
 *   - Outgoing always fades 1 → 0 in 200ms (faster than incoming so
 *     they don't overlap awkwardly with mode="wait").
 *
 * Two skip-animation paths:
 *   1. The user has prefers-reduced-motion enabled (a11y, mandatory).
 *   2. The transition crosses a theme boundary (light → dark or
 *      dark → light). Sliding between themes flashes background color
 *      mid-transition, which looks broken. Every active route in the
 *      funnel is currently 'light', so this branch is effectively a
 *      no-op today; kept for safety in case a future intake screen
 *      uses the dark theme.
 *
 * Pattern note: AnimatePresence requires Routes to receive a *captured*
 * `location` (not the live one from useLocation), otherwise the exiting
 * route re-renders with the new URL. That's why this component passes
 * `location` as a prop into a child component keyed on pathname — each
 * mount captures its own location for the duration of its lifetime.
 */

const ROUTE_THEMES = {
  '/signup': 'light',
  '/signup/account': 'light',
  '/signup/community': 'light',
  '/signup/academy/book': 'light',
  '/signup/venue/book': 'light',
  '/signup/callback': 'light',
  '/signup/setup/locations': 'light',
  '/signup/setup/size': 'light',
  '/signup/setup/policies': 'light',
}

// Single bucket for the entire community wizard subtree. Collapsing
// all 3 wizard pathnames to one key means the route-level
// AnimatePresence reuses the same motion.div across within-wizard
// navigations — which is what lets SetupWizardLayout (mounted as a
// parent route below) stay alive between steps so its progress bar
// can animate. Boundary in/out (community → wizard, wizard →
// welcome) still fires as usual because the key changes.
const WIZARD_KEY = '/signup/setup/*'
const VENUE_BOOK_KEY = '/signup/venue/book'

function routeKey(pathname) {
  if (pathname.startsWith('/signup/setup/')) return WIZARD_KEY
  if (pathname === '/signup/venue/book') return VENUE_BOOK_KEY
  if (pathname.startsWith('/signup/venue/')) return VENUE_BOOK_KEY
  return pathname
}

const FORWARD_ORDER = [
  '/signup',
  '/signup/account',
  // /signup/community, /signup/academy/book, and the venue wizard
  // are parallel persona forks at the same depth — the user only
  // ever hits one. Sharing an index with /signup/community keeps
  // the slide direction sane when stepping back to /signup/account
  // from any branch.
  '/signup/community',
  '/signup/academy/book',
  VENUE_BOOK_KEY,
  WIZARD_KEY,
]

const EASE_OUT_EXPO = [0.16, 1, 0.3, 1]
const ENTER_MS = 0.35
const EXIT_MS = 0.2

export default function SignupRoutes() {
  const location = useLocation()
  const reduceMotion = useReducedMotion()
  const prevPathRef = useRef(location.pathname)

  const prevPath = prevPathRef.current
  const prevTheme = ROUTE_THEMES[prevPath] || 'light'
  const currTheme = ROUTE_THEMES[location.pathname] || 'light'

  const prevKey = routeKey(prevPath)
  const currKey = routeKey(location.pathname)

  const prevIdx = FORWARD_ORDER.indexOf(prevKey)
  const currIdx = FORWARD_ORDER.indexOf(currKey)
  // Direction: +1 forward, -1 back. Unknown routes (callback,
  // legacy redirects) default to forward — they're terminal nodes
  // off the main path.
  const direction =
    prevIdx === -1 || currIdx === -1 ? 1 : currIdx >= prevIdx ? 1 : -1

  useEffect(() => {
    prevPathRef.current = location.pathname
  })

  // Wrapper bg covers the brief gap between exit and enter under
  // mode="wait" so we don't flash through the dark site bg.
  const wrapperBg =
    currTheme === 'dark' ? 'bg-black' : 'bg-[var(--signup-bg-base)]'
  const wrapperCls = `${wrapperBg} min-h-screen min-h-[100svh]`

  if (reduceMotion || prevTheme !== currTheme) {
    return (
      <div className={wrapperCls}>
        <FunnelRoutes location={location} />
      </div>
    )
  }

  return (
    <div className={wrapperCls}>
      <AnimatePresence mode="wait" initial={false}>
        <AnimatedRoute
          key={currKey}
          location={location}
          direction={direction}
        />
      </AnimatePresence>
    </div>
  )
}

/* ─── Per-route motion shell ─── */

function AnimatedRoute({ location, direction }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: direction > 0 ? 20 : -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{
        opacity: 0,
        transition: { duration: EXIT_MS, ease: EASE_OUT_EXPO },
      }}
      transition={{ duration: ENTER_MS, ease: EASE_OUT_EXPO }}
    >
      <FunnelRoutes location={location} />
    </motion.div>
  )
}

/* ─── The actual route table ───
   Receives a *captured* location prop (not from useLocation) so that
   exiting AnimatePresence children keep showing their original URL's
   content while they animate out. */

function FunnelRoutes({ location }) {
  return (
    <Routes location={location}>
      <Route index                 element={<SignupStart />} />
      <Route path="account"        element={<AcademyAccountGuard><SignupAuth /></AcademyAccountGuard>} />
      <Route path="community" element={<Navigate to="/upload" replace />} />
      <Route path="academy/book"   element={<AcademySignupGuard><SignupAcademyBook /></AcademySignupGuard>} />
      <Route path="venue/book"     element={<VenueSignupGuard><SignupVenueBook /></VenueSignupGuard>} />
      <Route path="callback"       element={<SignupCallback />} />
      {/* Legacy: the old academy stub at /signup/essentials was
          replaced by the single-page intake + Calendly embed at
          /signup/academy/book. Catch deep-links / bookmarks /
          stale sessionStorage routeForStep() lookups so they land
          on the new flow. */}
      <Route
        path="essentials"
        element={<Navigate to="/signup/academy/book" replace />}
      />
      {/* Legacy: venue wizard and old Calendly stub → book page */}
      <Route
        path="venue-contact"
        element={<Navigate to="/signup/venue/book" replace />}
      />
      <Route
        path="venue/identity"
        element={<Navigate to="/signup/venue/book" replace />}
      />
      <Route
        path="venue/courts"
        element={<Navigate to="/signup/venue/book" replace />}
      />
      {/* Legacy redirects — old wizard step routes that have since
          been removed/absorbed. All sit outside the SetupWizardLayout
          parent so the wizard chrome doesn't mount-then-unmount
          during the redirect.
            schedule: removed entirely → lands on the new first step.
            invite:   absorbed into the size step (now the "Community"
                      step) → lands there so any deep link / stale
                      sessionStorage resume keeps the same affordances.
            branding: replaced by the policies step (visibility +
                      WhatsApp). Logo capture moved to /signup/community,
                      accent customization was dropped entirely. */}
      <Route
        path="setup/schedule"
        element={<Navigate to="/signup/setup/locations" replace />}
      />
      <Route
        path="setup/invite"
        element={<Navigate to="/signup/setup/size" replace />}
      />
      <Route
        path="setup/branding"
        element={<Navigate to="/signup/setup/policies" replace />}
      />
      {/* Wizard subtree — SetupWizardLayout is mounted ONCE here as a
          parent route. React Router reuses it across the 3 child step
          routes, so its persistent progress bar can animate smoothly
          between widths instead of remounting at each new value. The
          enclosing AnimatePresence reuses the wrapper motion.div for
          all wizard pathnames (see WIZARD_KEY above) so the parent
          element identity doesn't get blown away either. */}
      <Route path="setup" element={<CommunitySignupGuard><SetupWizardLayout /></CommunitySignupGuard>}>
        <Route path="locations" element={<SetupWizardLocations />} />
        <Route path="size"      element={<SetupWizardSize />} />
        <Route path="policies"  element={<SetupWizardPolicies />} />
      </Route>
    </Routes>
  )
}
